import { ChatStatus, SourceDocumentUIPart, SourceUrlUIPart } from "ai";
import { isActionablePendingApproval } from "./approval/approvalGate";
import type {
  NoxusMessageFeedback,
  NoxusUIMessage,
} from "./noxus-message-types";
import { useState, useCallback } from "react";
import { useTranslations } from "next-intl";
import { useAtom } from "jotai";
import { debugSheetPayloadAtom } from "./debug-sheet-store";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  RefreshCcwIcon,
  ThumbsUpIcon,
  ThumbsDownIcon,
  CopyIcon,
  BookOpenIcon,
  BugIcon,
  DollarSignIcon,
} from "lucide-react";
import {
  MessageAction,
  MessageActions,
} from "@/components/ai-elements/message";
import { WebReferenceMetadata } from "@/generated";
import { Button } from "../shadcn/button";
import { Textarea } from "../shadcn/textarea";
import { SpotToast } from "../SpotToast";
import { copyToClipboard } from "@/utils/generic";
import { analytics_track, ANALYTICS_EVENTS } from "@/utils/mixpanel";
import { ConversationsService } from "@/generated/sdk.gen";
import { PublicConversationService } from "@/services/public/Conversation";
import { cn } from "@/lib/utils";
import { getAllSources, getTextFromParts, MessagePart } from "./utils";
import { useChatDisplayOptionsContext } from "./useChatDisplayOptions";
import { DebugSheet } from "./DebugSheet";
import { ConversationCostSheet } from "./ConversationCostSheet";
import { useCanViewConversationCosts } from "@/hooks/useCanViewConversationCosts";
import { useSpotUser } from "@/utils/spotUser";
import { TestThisReplyAction } from "./TestThisReplyAction";

interface NoxusMessageActionsProps {
  isLastMessage: boolean;
  status: ChatStatus;
  parts: MessagePart[];
  role: NoxusUIMessage["role"];
  messageId: string;
  /** The DB row this message was saved under. Feedback, debug and cost
   * address the row by it; without it (a turn stopped before it was saved)
   * those actions stay hidden rather than guess at a row. */
  dbMessageId?: string;
  messageIndex: number;
  messages: NoxusUIMessage[];
  regenerate: () => any;
  groupId: string;
  agentId: string;
  conversationId: string;
  setSourcesDrawerOpen: (
    sources: (SourceUrlUIPart | SourceDocumentUIPart)[]
  ) => void;
  // Set in the public widget: routes the feedback mutation through the public
  // token endpoint. When present with feedbackOnly, only the thumbs + copy
  // controls render (Retry/Debug/Cost/Sources are internal-only affordances).
  publicMetadata?: {
    group_id: string;
    token: string;
    deployment_id?: string;
    auth_token?: string;
  };
  feedbackOnly?: boolean;
}

export function NoxusMessageActions({
  regenerate,
  isLastMessage,
  status,
  parts,
  role,
  messageId,
  dbMessageId,
  messageIndex,
  messages,
  groupId,
  agentId,
  conversationId,
  setSourcesDrawerOpen,
  publicMetadata,
  feedbackOnly,
}: NoxusMessageActionsProps) {
  const t = useTranslations("widget");
  const {
    isCoworker,
    citationStyle,
    feedback,
    hideRetry,
    offerTestThisReply,
    showMessageFeedback,
  } = useChatDisplayOptionsContext();
  const { spotUser, isOnPrem } = useSpotUser();
  const canViewLogs = isCoworker && (spotUser.is_admin || isOnPrem);
  // `undefined` means "not touched this session" — fall back to what the
  // server replayed on the message so the thumb survives a reload.
  const [localFeedback, setLocalFeedback] = useState<
    NoxusMessageFeedback | null | undefined
  >(undefined);
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");

  const storedFeedback = messages[messageIndex]?.metadata?.feedback ?? null;
  const currentFeedback =
    localFeedback === undefined ? storedFeedback : localFeedback;
  const isLiked = currentFeedback?.is_positive === true;
  const isDisliked = currentFeedback?.is_positive === false;

  const isStreaming = status === "streaming" || status === "submitted";
  const hasTextContent = parts.some(
    (part) => part.type === "text" || part.type === "reasoning"
  );
  // A turn that failed before the first token has only its error banner. Debug
  // and Cost are exactly what someone wants there, and gating the whole bar on
  // text meant a failed turn offered nothing at all.
  const hasErrorContent = parts.some((part) => part.type === "data-error");
  const nextMessage = messages[messageIndex + 1];
  const isLastBeforeUser =
    isLastMessage || !nextMessage || nextMessage.role === "user";
  // A card still waiting on the user keeps the turn open: nothing to retry,
  // rate or copy yet.
  const awaitsApproval = parts.some(
    (part) =>
      part.type.startsWith("tool-") &&
      "state" in part &&
      isActionablePendingApproval(part)
  );
  const shouldShowActions =
    role === "assistant" &&
    (hasTextContent || hasErrorContent) &&
    isLastBeforeUser &&
    !awaitsApproval &&
    !(isLastMessage && isStreaming);
  // Rating a failure, or copying a banner, is not what the error bar is for.
  const showTextOnlyActions = hasTextContent;

  const feedbackMutation = useMutation({
    mutationFn: async (vars: { feedbackType: string; comment?: string }) => {
      if (!dbMessageId) {
        throw new Error("message has no DB row to rate");
      }
      if (publicMetadata) {
        return await PublicConversationService.addMessageFeedback({
          spotGroupId: groupId,
          conversationId,
          messageId: dbMessageId,
          assistantId: agentId,
          token: publicMetadata.token,
          deploymentId: publicMetadata.deployment_id,
          authToken: publicMetadata.auth_token,
          feedbackType: vars.feedbackType,
          comment: vars.comment,
        });
      }
      return await ConversationsService.addMessageFeedback({
        path: {
          group_id: groupId,
          conversation_id: conversationId,
          message_id: dbMessageId,
        },
        body: {
          feedback_type: vars.feedbackType,
          comment: vars.comment,
        },
      } as any);
    },
    onSuccess: (_res, vars) => {
      const positive = vars.feedbackType === "Positive";
      setLocalFeedback({ is_positive: positive });
      setShowFeedbackInput(false);
      setFeedbackText("");
      analytics_track(ANALYTICS_EVENTS.MESSAGE_FEEDBACK, {
        is_positive: positive,
        has_comment: !!vars.comment,
        conversation_id: conversationId,
      });
      SpotToast.success({
        title: t("feedback.submittedTitle"),
        description: t("feedback.submittedDescription"),
      });
    },
    onError: (error) => {
      console.error("Error submitting feedback:", error);
      SpotToast.error({
        title: t("feedback.submitErrorTitle"),
        description: t("feedback.tryAgainLater"),
      });
    },
  });

  const removeFeedbackMutation = useMutation({
    mutationFn: async () => {
      if (!dbMessageId) {
        throw new Error("message has no DB row to rate");
      }
      if (publicMetadata) {
        return await PublicConversationService.removeMessageFeedback({
          spotGroupId: groupId,
          conversationId,
          messageId: dbMessageId,
          assistantId: agentId,
          token: publicMetadata.token,
          deploymentId: publicMetadata.deployment_id,
          authToken: publicMetadata.auth_token,
        });
      }
      return await ConversationsService.removeMessageFeedback({
        path: {
          group_id: groupId,
          conversation_id: conversationId,
          message_id: dbMessageId,
        },
      });
    },
    onSuccess: () => {
      setLocalFeedback(null);
      setShowFeedbackInput(false);
      setFeedbackText("");
      analytics_track(ANALYTICS_EVENTS.MESSAGE_FEEDBACK_REMOVED, {
        conversation_id: conversationId,
      });
      SpotToast.success(t("feedback.removed"));
    },
    onError: (error) => {
      console.error("Error removing feedback:", error);
      SpotToast.error({
        title: t("feedback.removeErrorTitle"),
        description: t("feedback.tryAgainLater"),
      });
    },
  });

  const handleCopyToClipboard = () => {
    const text = getTextFromParts(parts, "\n");
    if (!text) {
      SpotToast.error(t("actions.noTextToCopy"));
      return;
    }
    copyToClipboard(text);
  };

  const pendingType = feedbackMutation.isPending
    ? feedbackMutation.variables?.feedbackType
    : undefined;

  const isMutating =
    feedbackMutation.isPending || removeFeedbackMutation.isPending;

  // Upvote submits immediately (one click); downvote opens a details panel that
  // records the vote on submit. Clicking the thumb that is already active
  // clears the rating, so a mistaken vote can always be taken back.
  const handleLike = () => {
    if (isMutating) return;
    if (isLiked) {
      removeFeedbackMutation.mutate();
      return;
    }
    setShowFeedbackInput(false);
    setFeedbackText("");
    feedbackMutation.mutate({ feedbackType: "Positive" });
  };

  const handleDislike = () => {
    if (isMutating) return;
    if (isDisliked) {
      removeFeedbackMutation.mutate();
      return;
    }
    setShowFeedbackInput((v) => !v);
  };

  const dismissFeedback = () => {
    setShowFeedbackInput(false);
    setFeedbackText("");
  };

  const submitFeedback = () => {
    feedbackMutation.mutate({
      feedbackType: "Negative",
      comment: feedbackText.trim() || undefined,
    });
  };

  const [debugPayload, setDebugPayload] = useAtom(debugSheetPayloadAtom);
  const isDebugOpen = debugPayload?.messageId === messageId;
  const [isLoadingDebug, setIsLoadingDebug] = useState(false);
  const [debugData, setDebugData] = useState<Record<string, unknown> | null>(
    null
  );
  const handleDebug = useCallback(async () => {
    if (!groupId || !conversationId || !dbMessageId) {
      return;
    }

    if (isDebugOpen) {
      setDebugPayload(null);
      return;
    }

    const showData = (data: Record<string, unknown> | null) =>
      setDebugPayload({
        messageId,
        groupId,
        conversationId,
        canViewLogs,
        data,
      });

    if (debugData) {
      showData(debugData);
      return;
    }

    setIsLoadingDebug(true);
    try {
      const response = await ConversationsService.getMessageDebugInfo({
        path: {
          group_id: groupId,
          conversation_id: conversationId,
        },
        query: { message_id: dbMessageId },
      });

      const data = response.data as Record<string, unknown>;
      setDebugData(data);
      showData(data);
    } catch (error) {
      console.error("Error fetching debug data:", error);
      SpotToast.error({
        title: "Error fetching debug data",
        description: "Please try again later",
      });
    } finally {
      setIsLoadingDebug(false);
    }
  }, [
    groupId,
    conversationId,
    dbMessageId,
    messageId,
    canViewLogs,
    debugData,
    isDebugOpen,
    setDebugPayload,
  ]);

  const canViewCosts = useCanViewConversationCosts();
  const costEnabled = isCoworker && canViewCosts;
  const [isCostOpen, setIsCostOpen] = useState(false);
  const canAddressRow = !!dbMessageId;

  if (!shouldShowActions) {
    return null;
  }

  return (
    <>
      <MessageActions className="mb-4">
        {/* Latest reply only: the endpoint replaces the conversation's last
            turn, and a send carries only that turn (see latestTurnOnly). */}
        {!feedbackOnly && !hideRetry && isLastMessage && (
          <MessageAction
            label={t("actions.retry")}
            onClick={regenerate}
            tooltip={t("actions.regenerate")}
          >
            <RefreshCcwIcon className="size-4 stroke-ring" />
          </MessageAction>
        )}
        {showMessageFeedback && showTextOnlyActions && canAddressRow && (
          <>
            <MessageAction
              label={t("actions.like")}
              onClick={handleLike}
              aria-pressed={isLiked}
              tooltip={
                isLiked
                  ? t("actions.removeFeedback")
                  : t("actions.likeResponse")
              }
              loading={
                pendingType === "Positive" ||
                (isLiked && removeFeedbackMutation.isPending)
              }
            >
              <ThumbsUpIcon
                className={cn("size-4 stroke-ring", isLiked && "fill-ring")}
              />
            </MessageAction>
            <MessageAction
              label={t("actions.dislike")}
              onClick={handleDislike}
              aria-pressed={isDisliked}
              tooltip={
                isDisliked
                  ? t("actions.removeFeedback")
                  : showFeedbackInput
                    ? t("actions.cancel")
                    : t("actions.dislikeResponse")
              }
              loading={isDisliked && removeFeedbackMutation.isPending}
            >
              <ThumbsDownIcon
                className={cn(
                  "size-4 stroke-ring",
                  (isDisliked || showFeedbackInput) && "fill-ring"
                )}
              />
            </MessageAction>
          </>
        )}
        {showTextOnlyActions && (
          <MessageAction
            label={t("actions.copy")}
            onClick={handleCopyToClipboard}
            tooltip={t("actions.copyToClipboard")}
          >
            <CopyIcon className="size-4 stroke-ring" />
          </MessageAction>
        )}
        {costEnabled && canAddressRow && (
          <MessageAction
            label={t("actions.cost")}
            onClick={() => setIsCostOpen(true)}
            tooltip={t("actions.viewCost")}
          >
            <DollarSignIcon className="size-4 stroke-ring" />
          </MessageAction>
        )}
        {canViewLogs && canAddressRow && (
          <MessageAction
            label={t("actions.debug")}
            onClick={handleDebug}
            tooltip={t("actions.debugResponse")}
            loading={isLoadingDebug}
          >
            <BugIcon className="size-4 stroke-ring" />
          </MessageAction>
        )}
        {offerTestThisReply && (
          <TestThisReplyAction
            agentId={agentId}
            conversationId={conversationId}
            dbMessageId={dbMessageId}
            parts={parts}
            messageIndex={messageIndex}
            messages={messages}
          />
        )}
        {!feedbackOnly && citationStyle !== "hidden" && (
          <SourcesButton
            parts={parts}
            setSourcesDrawerOpen={setSourcesDrawerOpen}
          />
        )}
      </MessageActions>

      {showFeedbackInput && !isDisliked && (
        <div className="mt-2 flex max-w-[420px] flex-col rounded-md border border-input shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 dark:bg-input/30">
          <Textarea
            autoFocus
            rows={1}
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder={feedback.placeholder}
            className="max-h-[120px] min-h-[36px] resize-none border-0 bg-transparent px-3 py-2 text-sm shadow-none focus-visible:ring-0"
          />
          <div className="flex items-center justify-end gap-2 px-2 pb-2">
            <Button variant="ghost" size="sm" onClick={dismissFeedback}>
              {feedback.cancelLabel}
            </Button>
            <Button
              size="sm"
              loading={feedbackMutation.isPending}
              onClick={submitFeedback}
            >
              {feedback.submitLabel}
            </Button>
          </div>
        </div>
      )}

      {costEnabled && (
        <ConversationCostSheet
          isOpen={isCostOpen}
          onOpenChange={setIsCostOpen}
          conversationId={conversationId}
          initialExpandedMessageId={dbMessageId}
        />
      )}
    </>
  );
}

const SourcesButton = ({
  parts,
  setSourcesDrawerOpen,
}: {
  parts: MessagePart[];
  setSourcesDrawerOpen: (
    sources: (SourceUrlUIPart | SourceDocumentUIPart)[]
  ) => void;
}) => {
  const t = useTranslations("widget.actions");
  const sources = getAllSources(parts);
  const sourceImages = sources
    .map((source) => {
      const meta = source.providerMetadata?.noxus as WebReferenceMetadata;
      return meta?.image_url;
    })
    .filter((url): url is string => !!url)
    .slice(0, 3);

  if (sources.length === 0) {
    return null;
  }
  return (
    <Button
      onClick={() => setSourcesDrawerOpen(sources)}
      variant="ghost"
      className="gap-1 items-center text-ring hover:text-ring"
    >
      {sourceImages.length > 0 ? (
        <div className="flex gap-0.5">
          {sourceImages.map((imageUrl, idx) => (
            <img
              key={idx}
              src={imageUrl}
              alt=""
              className="size-4 object-cover rounded border"
            />
          ))}
        </div>
      ) : (
        <BookOpenIcon className="size-4 text-ring stroke-ring" />
      )}
      {t("sources", { count: sources.length })}
    </Button>
  );
};
